/**
 * The rail row under the mode toggle: this brand's AI writing credits and a way to buy more.
 *
 * Reads `remaining`/`resetsAt` from the same `CreditView` the composer and the settings panel
 * already show — see `shared/src/aiCredits.ts` and `backend/src/modules/ai/service.ts`. Buying is
 * `OWNER`/`ADMIN` only, same as `modules/ai/routes.ts#postCreditCheckout`; an editor sees the
 * balance and no button, rather than a button that would 403.
 */

import { useState } from 'react'
import type { ReactElement } from 'react'

import { cn } from '../lib/cn'
import { useAuth } from '../lib/auth'
import { useT, useFormat } from '../lib/i18n'
import { useOrgDetail } from '../features/settings/api'
import { useAiCreditBalance } from '../features/ai/queries'
import { AiCreditsBuyDialog } from '../features/ai/AiCreditsBuyDialog'
import { Icon } from '../components/ui/Icon'
import { Skeleton } from '../components/ui/Skeleton'
import { Tooltip } from '../components/ui/Tooltip'

export interface AiCreditsNavRowProps {
  expanded: boolean
}

/** Same slot the real row fills, so the rail doesn't jump once the first fetch lands. */
const LoadingRow = ({ expanded }: { expanded: boolean }): ReactElement => (
  <div
    className={cn(
      'flex w-full items-center border-t border-chrome-600 py-2.5',
      expanded ? 'justify-start gap-3 px-4' : 'flex-col gap-1.5 px-1',
    )}
  >
    <Icon name="sparkle" size={20} className="shrink-0 text-chrome-mute" />
    {expanded ? (
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton width={96} height={13} />
        <Skeleton width={64} height={10} />
      </div>
    ) : null}
  </div>
)

export const AiCreditsNavRow = ({ expanded }: AiCreditsNavRowProps): ReactElement | null => {
  const t = useT()
  const f = useFormat()
  const { activeBrand } = useAuth()
  const org = useOrgDetail()
  const brandId = activeBrand?.id ?? null
  const credits = useAiCreditBalance(brandId)
  const [buyOpen, setBuyOpen] = useState(false)

  if (brandId === null) return null
  if (credits.isLoading) return <LoadingRow expanded={expanded} />
  if (!credits.data) return null

  const data = credits.data
  const canBuy = org.data?.role === 'OWNER' || org.data?.role === 'ADMIN'

  const count = t('shell.aiCredits.count', { remaining: f.integer(data.remaining) })
  const resetsNote = t('shell.aiCredits.resetsNote', { resetsAt: f.date(data.resetsAt) })
  const summary = `${count} · ${resetsNote}`

  const row = (
    <div
      className={cn(
        'flex w-full items-center border-t border-chrome-600 py-2.5 text-chrome-mute',
        expanded ? 'justify-start gap-3 px-4' : 'flex-col gap-1.5 px-1',
      )}
    >
      <Icon name="sparkle" size={20} className="shrink-0" />
      {expanded ? (
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="t-small truncate text-chrome-ink">{count}</span>
          <span className="t-label truncate text-chrome-mute">{resetsNote}</span>
          {canBuy ? (
            <button
              type="button"
              onClick={() => {
                setBuyOpen(true)
              }}
              className="ring-focus-inset t-label self-start text-brass hover:underline"
            >
              {t('shell.aiCredits.buyButton')}
            </button>
          ) : null}
        </div>
      ) : canBuy ? (
        <button
          type="button"
          onClick={() => {
            setBuyOpen(true)
          }}
          className="ring-focus-inset t-label text-brass"
        >
          {t('shell.aiCredits.buyButtonShort')}
        </button>
      ) : null}
    </div>
  )

  return (
    <>
      {expanded ? row : <Tooltip content={summary} placement="right">{row}</Tooltip>}
      {canBuy ? (
        <AiCreditsBuyDialog
          open={buyOpen}
          onClose={() => {
            setBuyOpen(false)
          }}
          brandId={brandId}
          balance={data}
        />
      ) : null}
    </>
  )
}
